/**
 * History Store — task history, session history, stats, and dashboard filters.
 *
 * Replaces: history-related state from SocketContext.tsx + TaskHistoryContext.tsx (56 lines)
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { EVENTS } from "@/constants/events";
import type { BoardTaskDelta, StoryEntryDelta } from "@bash/shared";
import type {
  PersistedSession,
  PersistedTask,
  TaskStats,
  TaskQueryFilters,
  DashboardDateFilter,
  DashboardStatusFilter,
} from "./types";
import { getSocket } from "./socketService";
import { useTaskStore, vouchForOwnTask } from "./taskStore";

/**
 * Carry the Activity-card heat fields across a stats write that does not carry
 * them. Only a read that asked for `heatDays` returns doneAt/doneTotal, but the
 * bundled `tasks:list` stats (initial load, every filter/search change, every
 * silent reconnect refresh) never ask — so a wholesale write would erase a
 * heatmap the card had already loaded. The keys stay ABSENT, never `undefined`,
 * when neither side has them: absence is the contract the server ships.
 * A heat-bearing response always wins, including an empty window / zero total.
 */
export function withHeat(next: TaskStats, prev: TaskStats | null): TaskStats {
  const doneAt = next.doneAt ?? prev?.doneAt;
  const doneTotal = next.doneTotal ?? prev?.doneTotal;
  // The truncation flag travels WITH the window it describes: a fresh heat
  // read replaces it (absent = not truncated), a heat-less write keeps it.
  const doneAtTruncated = next.doneAt
    ? next.doneAtTruncated
    : prev?.doneAtTruncated;
  return {
    ...next,
    ...(doneAt ? { doneAt } : {}),
    ...(doneTotal !== undefined ? { doneTotal } : {}),
    ...(doneAtTruncated ? { doneAtTruncated } : {}),
  };
}

// Newest delta `at` applied per task — the ordering guard. Module-level on
// purpose: it is transport bookkeeping, not board state, and a tasks:list
// swap must NOT reset it (a fetched row is newer than any dropped delta).
let boardDeltaAt = new Map<string, string>();
let restSnapshotAt = new Map<string, string>();
// Newest story seq applied per task — same transport bookkeeping, same rules.
let storyEntrySeq = new Map<string, number>();

/** Test-only. */
export function _resetBoardDeltaGuard(): void {
  boardDeltaAt = new Map();
  restSnapshotAt = new Map();
  storyEntrySeq = new Map();
}

interface HistoryStore {
  // State
  sessionHistory: PersistedSession[];
  taskHistory: PersistedTask[];
  historyLoading: boolean;
  taskHistoryLoaded: boolean;
  taskStats: TaskStats | null;
  hasMoreTasks: boolean;

  // Dashboard filters (persisted across navigation)
  dashboardSearch: string;
  dashboardDateFilter: DashboardDateFilter;
  dashboardStatusFilter: DashboardStatusFilter;

  // Internal pagination
  _taskOffset: number;
  _taskFilters: TaskQueryFilters;
  _fetchGeneration: number;

  // Actions
  loadSessionHistory: () => void;
  loadTaskHistory: (options?: {
    reset?: boolean;
    search?: string;
    dateFilter?: DashboardDateFilter;
    statusFilter?: DashboardStatusFilter;
    /**
     * Silent reconnect refresh: don't toggle `historyLoading` (no spinner flash)
     * and only swap `taskHistory` if the list actually changed (no reflow unless
     * something genuinely changed while disconnected). Used for bare reconnects.
     */
    silent?: boolean;
  }) => void;
  loadTaskStats: (options?: {
    dateFilter?: DashboardDateFilter;
    search?: string;
    statusFilter?: DashboardStatusFilter;
    /** 1–120: also ask for `doneAt` over that window (Bash home Activity card). */
    heatDays?: number;
  }) => void;
  loadMoreTasks: () => void;
  resumeSession: (sessionId: string) => void;

  setDashboardSearch: (search: string) => void;
  setDashboardDateFilter: (filter: DashboardDateFilter) => void;
  setDashboardStatusFilter: (filter: DashboardStatusFilter) => void;

  /** home-streams: apply one board:task_delta as a HINT. Ordering-guarded;
   *  a tasks:list response always wins wholesale. */
  applyBoardDelta: (delta: BoardTaskDelta) => void;
  /** story spine: apply one story:entry as a HINT (seq-guarded per task);
   *  rows heal on the next tasks:list read. */
  applyStoryEntry: (delta: StoryEntryDelta) => void;

  // Internal — called by socketHandlers
  _setTaskHistory: (
    updater: (prev: PersistedTask[]) => PersistedTask[]
  ) => void;
  _setTaskStats: (
    updater: (prev: TaskStats | null) => TaskStats | null
  ) => void;
}

export const useHistoryStore = create<HistoryStore>()(
  devtools(
    (set, get) => ({
      sessionHistory: [],
      taskHistory: [],
      historyLoading: false,
      taskHistoryLoaded: false,
      taskStats: null,
      hasMoreTasks: false,

      dashboardSearch: "",
      dashboardDateFilter: "all",
      dashboardStatusFilter: "all",

      _taskOffset: 0,
      _taskFilters: {},
      _fetchGeneration: 0,

      // Internal setters for socketHandlers
      _setTaskHistory: (updater) =>
        set((state) => ({ taskHistory: updater(state.taskHistory) })),
      _setTaskStats: (updater) =>
        set((state) => ({ taskStats: updater(state.taskStats) })),

      loadTaskStats: (options) => {
        const socket = getSocket();
        if (!socket?.connected) return;
        const timezoneOffset = new Date().getTimezoneOffset();
        socket.emit(
          EVENTS.TASK_STATS,
          {
            dateFilter: options?.dateFilter,
            search: options?.search,
            statusFilter: options?.statusFilter,
            timezoneOffset,
            // Absent unless asked — every other caller keeps the shipped payload.
            ...(options?.heatDays ? { heatDays: options.heatDays } : {}),
          },
          (response: { success: boolean; stats?: TaskStats }) => {
            if (response.success && response.stats) {
              set((state) => ({
                taskStats: withHeat(response.stats!, state.taskStats),
              }));
            }
          }
        );
      },

      loadTaskHistory: (options = {}) => {
        const socket = getSocket();
        if (!socket?.connected) return;

        const shouldReset = options.reset ?? true;
        const { _taskOffset, _fetchGeneration } = get();
        const offset = shouldReset ? 0 : _taskOffset;

        if (shouldReset) {
          set({
            _fetchGeneration: _fetchGeneration + 1,
            _taskFilters: {
              search: options.search,
              dateFilter: options.dateFilter,
              statusFilter: options.statusFilter,
            },
            _taskOffset: 0,
          });
        }

        const generation = shouldReset
          ? _fetchGeneration + 1
          : _fetchGeneration;
        // Silent reconnect refresh skips the loading flag — no spinner flash on a
        // tab refocus; the list is only swapped below if it actually changed.
        if (!options.silent) set({ historyLoading: true });
        const timezoneOffset = new Date().getTimezoneOffset();

        // On initial load (reset + offset 0), include stats to avoid a separate round-trip
        const includeStats = shouldReset && offset === 0;

        socket.emit(
          "tasks:list",
          {
            limit: 40,
            offset,
            search: options.search ?? get()._taskFilters.search,
            dateFilter: options.dateFilter ?? get()._taskFilters.dateFilter,
            statusFilter:
              options.statusFilter ?? get()._taskFilters.statusFilter,
            timezoneOffset,
            includeStats,
            // Plain recency, the archive's order (founder 09-18: "just show the
            // latest 40 tasks in that order, with needs you separated in a
            // different tab"). The default 'priority' order RESERVES the page
            // for every awaiting/error/queued row before any finished one, so
            // 37 stale rows left 3 slots and a fortnight of real work never
            // reached the browser. What needs him lives in its own tab, fed by
            // the interactions wire — it does not need slots here to be seen.
            order: "recent" as const,
          },
          (response: {
            success: boolean;
            tasks?: PersistedTask[];
            hasMore?: boolean;
            total?: number;
            stats?: TaskStats;
          }) => {
            // Drop stale callbacks
            if (generation !== get()._fetchGeneration) return;

            set({ historyLoading: false, taskHistoryLoaded: true });
            if (response.success && response.tasks) {
              // The list is the user's own tasks: each row is proof that
              // lifts a no-access belief formed from one refusal.
              for (const t of response.tasks) vouchForOwnTask(t.id);
              if (shouldReset) {
                if (options.silent) {
                  // Only swap if the list genuinely changed while we were away, so a
                  // bare reconnect with no server-side changes causes zero re-render.
                  const current = get().taskHistory;
                  const changed =
                    current.length !== response.tasks.length ||
                    response.tasks.some((t, i) => t.id !== current[i]?.id);
                  if (changed) set({ taskHistory: response.tasks });
                } else {
                  set({ taskHistory: response.tasks });
                }
              } else {
                set((state) => ({
                  taskHistory: [...state.taskHistory, ...response.tasks!],
                }));
              }
              const newOffset = offset + response.tasks.length;
              set({
                _taskOffset: newOffset,
                hasMoreTasks: response.hasMore ?? false,
              });

              // Populate stats from bundled response (avoids separate TASK_STATS round-trip).
              // This read never asks for heatDays — withHeat keeps the Activity card's
              // fields alive across it.
              if (response.stats) {
                set((state) => ({
                  taskStats: withHeat(response.stats!, state.taskStats),
                }));
              }
            }
          }
        );
      },

      loadMoreTasks: () => {
        const {
          hasMoreTasks,
          historyLoading,
          taskHistoryLoaded,
          loadTaskHistory,
        } = get();
        if (!hasMoreTasks || historyLoading || !taskHistoryLoaded) return;
        loadTaskHistory({ reset: false });
      },

      loadSessionHistory: () => {
        const socket = getSocket();
        if (!socket?.connected) return;
        set({ historyLoading: true });
        socket.emit(
          "sessions:list",
          { limit: 50 },
          (response: { success: boolean; sessions?: PersistedSession[] }) => {
            set({ historyLoading: false });
            if (response.success && response.sessions) {
              set({ sessionHistory: response.sessions });
            }
          }
        );
      },

      resumeSession: (sessionId) => {
        const socket = getSocket();
        if (!socket?.connected) return;
        useTaskStore.getState()._setSelectedTaskId(sessionId);
        socket.emit(EVENTS.MESSAGE_SYNC_REQUEST, { taskId: sessionId });
      },

      applyBoardDelta: (delta) => {
        const newest = boardDeltaAt.get(delta.taskId);
        if (newest && delta.at <= newest) return; // ISO strings compare chronologically
        boardDeltaAt.set(delta.taskId, delta.at);

        // The rest snapshot (verdict/output doc) lands seconds AFTER the idle
        // delta; the wire carries only a hint, never the payload — a newer stamp
        // means the finished card upgraded, and one silent read is truth (the
        // same shape as the newborn path below).
        if (delta.restSnapshotAt) {
          const seen = restSnapshotAt.get(delta.taskId);
          if (!seen || delta.restSnapshotAt > seen) {
            restSnapshotAt.set(delta.taskId, delta.restSnapshotAt);
            get().loadTaskHistory({ silent: true });
          }
        }

        const { taskHistory } = get();
        const existing = taskHistory.find((t) => t.id === delta.taskId);
        if (!existing) {
          // A delta can't honestly fill PersistedTask's required fields — one
          // silent read is truth, and cheap at task-creation frequency.
          get().loadTaskHistory({ silent: true });
          return;
        }
        set({
          taskHistory: taskHistory.map((t) =>
            t.id === delta.taskId
              ? {
                  ...t,
                  lifecycleState: delta.lifecycleState,
                  title: delta.title ?? t.title,
                  pulseHeadline: delta.pulseHeadline ?? t.pulseHeadline,
                  lastActivityAt:
                    delta.lastActivityAt > t.lastActivityAt
                      ? delta.lastActivityAt
                      : t.lastActivityAt,
                }
              : t
          ),
        });
      },

      applyStoryEntry: (delta) => {
        const { entry } = delta;
        const last = storyEntrySeq.get(delta.taskId) ?? 0;
        if (entry.seq <= last) return; // per-task monotonic; stale push dropped
        storyEntrySeq.set(delta.taskId, entry.seq);

        const { taskHistory } = get();
        const existing = taskHistory.find((t) => t.id === delta.taskId);
        if (!existing) {
          // A lone entry can't fill PersistedTask's required fields — one silent
          // read is truth (same shape as applyBoardDelta's newborn path).
          get().loadTaskHistory({ silent: true });
          return;
        }
        const patch: Partial<PersistedTask> =
          entry.kind === "phase" && entry.phase
            ? { storyPhase: entry.phase }
            : entry.kind === "step"
              ? {
                  storyPhase: "working",
                  headline: entry.line ?? existing.headline,
                  headlineApp: entry.app ?? null,
                }
              : {};
        if (Object.keys(patch).length === 0) return;
        set({
          taskHistory: taskHistory.map((t) =>
            t.id === delta.taskId ? { ...t, ...patch } : t
          ),
        });
      },

      setDashboardSearch: (search) => set({ dashboardSearch: search }),
      setDashboardDateFilter: (filter) => set({ dashboardDateFilter: filter }),
      setDashboardStatusFilter: (filter) =>
        set({ dashboardStatusFilter: filter }),
    }),
    { name: "HistoryStore", enabled: process.env.NODE_ENV === "development" }
  )
);
